import React from 'react';
import { Icon } from '@repo/ui';
import type { TransformedInvoice } from '../types';
import type { TransactionDeliveryDetails, TransactionDocumentHistoryRow } from '../types/juniperTypes';
import { SMITH_TENANT_ID } from '../constants/tenants';
import { invoiceApi } from '../api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DrawerTab = 'details' | 'documents' | 'email' | 'history' | 'actions' | 'smith';

interface UtilityRailButton {
  id: DrawerTab;
  icon: string;
  label: string;
}

export interface TransactionDrawerActions {
  onViewInClio: (invoice: TransformedInvoice) => void;
  onViewPdf: (invoice: TransformedInvoice) => void;
  onDownloadReceipts: (invoice: TransformedInvoice) => void;
  onSendEmail: (invoice: TransformedInvoice) => void;
  onMarkCompleted: (invoice: TransformedInvoice) => void;
  onViewBankTransactions: (invoice: TransformedInvoice) => void;
  onRegeneratePdf: (invoice: TransformedInvoice) => void;
  onRefreshFromClio: (invoice: TransformedInvoice) => void;
  onVoidDeleteTrustRequest: (invoice: TransformedInvoice) => void;
  isResendEligible: (invoice: TransformedInvoice) => boolean;
  hasValidRecipients: (invoice: TransformedInvoice) => boolean;
  isPdfRegenerationEligible: (invoice: TransformedInvoice) => boolean;
  isMarkCompletedEligible: (invoice: TransformedInvoice) => boolean;
  isVoidDeleteEligible: (invoice: TransformedInvoice) => boolean;
  getVoidDeleteAction: (invoice: TransformedInvoice) => 'Delete' | 'Void' | null;
  isReprocessing: boolean;
  isResendPending: boolean;
  isRegeneratePending: boolean;
  isMarkCompletedPending: boolean;
  isVoidDeletePending: boolean;
}

export interface TransactionDrawerProps {
  /** The row whose data populates the drawer */
  activeRow: TransformedInvoice | null;
  isOpen: boolean;
  activeTab: DrawerTab;
  onTabChange: (tab: DrawerTab) => void;
  onClose: () => void;
  tenantId: string | undefined;
  /** Formatters passed from the parent so we don't duplicate logic */
  formatRawStatus: (state: string | null) => string;
  formatCurrency: (amount: number) => string;
  formatDateTime: (dateString: string | null | undefined) => { date: string; time: string };
  getTransactionWarnings: (invoice: TransformedInvoice) => string[];
  /** Action handlers for the Actions tab */
  actions?: TransactionDrawerActions;
  /** Delivery details for the Email tab */
  deliveryDetails?: TransactionDeliveryDetails | null;
  isLoadingDeliveryDetails?: boolean;
  /** Document history for the Documents tab */
  documentHistoryRows?: TransactionDocumentHistoryRow[];
  isLoadingDocumentHistory?: boolean;
}

// ---------------------------------------------------------------------------
// Utility Rail (icon sidebar on the far-right)
// ---------------------------------------------------------------------------

const RAIL_BUTTONS: UtilityRailButton[] = [
  { id: 'details', icon: 'HiInformationCircle', label: 'Details' },
  { id: 'documents', icon: 'HiDocumentDuplicate', label: 'Documents' },
  { id: 'email', icon: 'HiMail', label: 'Email' },
  { id: 'history', icon: 'FaHourglassHalf', label: 'History' },
  { id: 'actions', icon: 'HiCog', label: 'Actions' },
];

const SMITH_BUTTON: UtilityRailButton = {
  id: 'smith',
  icon: 'HiChat',
  label: 'SMITH',
};

const SMITH_TENANT = SMITH_TENANT_ID;

function UtilityRail({
  activeTab,
  isOpen,
  onTabChange,
  onClose,
  tenantId,
}: {
  activeTab: DrawerTab;
  isOpen: boolean;
  onTabChange: (tab: DrawerTab) => void;
  onClose: () => void;
  tenantId: string | undefined;
}) {
  const buttons = tenantId === SMITH_TENANT ? [...RAIL_BUTTONS, SMITH_BUTTON] : RAIL_BUTTONS;

  const handleClick = (tab: DrawerTab) => {
    if (isOpen && activeTab === tab) {
      onClose();
    } else {
      onTabChange(tab);
    }
  };

  return (
    <div className="flex flex-col items-center w-10 bg-xtnd-dark-50 dark:bg-xtnd-dark-800 border-l border-xtnd-dark-200 dark:border-xtnd-dark-600 py-2 gap-1 flex-shrink-0">
      {buttons.map(btn => (
        <button
          key={btn.id}
          type="button"
          onClick={() => handleClick(btn.id)}
          title={btn.label}
          className={`p-2 rounded-md transition-colors ${
            isOpen && activeTab === btn.id
              ? 'bg-xtnd-blue/10 text-xtnd-blue dark:text-xtnd-blue-300'
              : 'text-xtnd-dark-400 dark:text-xtnd-dark-300 hover:bg-xtnd-dark-100 dark:hover:bg-xtnd-dark-700 hover:text-xtnd-dark-600 dark:hover:text-xtnd-dark-100'
          }`}
        >
          <Icon name={btn.icon} className="h-5 w-5" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab Panels
// ---------------------------------------------------------------------------

function DetailsPanel({
  invoice,
  formatRawStatus,
  formatCurrency,
  formatDateTime,
  getTransactionWarnings,
}: {
  invoice: TransformedInvoice;
  formatRawStatus: (s: string | null) => string;
  formatCurrency: (a: number) => string;
  formatDateTime: (d: string | null | undefined) => { date: string; time: string };
  getTransactionWarnings: (inv: TransformedInvoice) => string[];
}) {
  const warnings = getTransactionWarnings(invoice);

  return (
    <div className="space-y-5 text-sm">
      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
          <div className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1.5">
            Warnings
          </div>
          <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-300">
            {warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span>&#9888;</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Matter */}
      <Section title="Matter">
        <Field
          label="Name"
          value={invoice.matterName || 'Unknown'}
          href={invoice.matterId && invoice.matterId !== 'Unknown' ? `https://app.clio.com/matters/${invoice.matterId}` : undefined}
        />
        <Field label="ID" value={invoice.matterId} mono />
        <Field
          label="Type"
          value={invoice.matterType || 'Unknown'}
          badgeClass={getMatterTypeBadgeClass(invoice.matterType)}
        />
        <Field label="Status" value={invoice.matterStatus || '-'} />
        <Field label="Client" value={invoice.client || 'Unknown'} />
      </Section>

      {/* Transaction */}
      <Section title="Transaction">
        <Field
          label="Juniper Status"
          value={formatRawStatus(invoice.juniperStatus)}
          badgeClass={getJuniperStatusBadgeClass(invoice.juniperStatus || '')}
        />
        <Field label="Created" value={formatDateTime(invoice.createdAt).date} />
        <Field label="Updated" value={formatDateTime(invoice.updatedAt).date} />
        <Field
          label="Last Sent"
          value={invoice.lastSentAt ? formatDateTime(invoice.lastSentAt).date : 'Not sent'}
        />
      </Section>

      {/* Operational Bill */}
      <Section title="Operational Bill" accentColor="blue">
        <Field
          label="Number"
          value={invoice.operationalBill?.invoiceNumber || 'N/A'}
          href={invoice.operationalBill?.id ? `https://app.clio.com/bills/${invoice.operationalBill.id}` : undefined}
        />
        <Field
          label="Status"
          value={formatRawStatus(invoice.operationalBill?.status)}
          badgeClass={getBillStatusBadgeClass(formatRawStatus(invoice.operationalBill?.status))}
        />
        <Field
          label="Total"
          value={
            invoice.operationalBill?.amount != null
              ? formatCurrency(invoice.operationalBill.amount)
              : '-'
          }
        />
        <Field
          label="Paid"
          value={
            invoice.operationalBill?.paid != null
              ? formatCurrency(invoice.operationalBill.paid)
              : '-'
          }
        />
        <Field
          label="Balance"
          value={
            invoice.operationalBill?.balance != null
              ? formatCurrency(invoice.operationalBill.balance)
              : '-'
          }
        />
        <Field
          label="Issued"
          value={
            invoice.operationalBill?.issuedDate
              ? formatDateTime(invoice.operationalBill.issuedDate).date
              : '-'
          }
        />
        <Field
          label="Due"
          value={
            invoice.operationalBill?.dueDate
              ? formatDateTime(invoice.operationalBill.dueDate).date
              : '-'
          }
        />
      </Section>

      {/* Trust Request */}
      <Section title="Trust Request" accentColor="green">
        <Field
          label="Number"
          value={invoice.trustRequest?.number || 'N/A'}
          href={invoice.trustRequest?.id ? `https://app.clio.com/bills/${invoice.trustRequest.id}` : undefined}
        />
        <Field
          label="State"
          value={formatRawStatus(invoice.trustRequest?.trrStatus)}
          badgeClass={getBillStatusBadgeClass(formatRawStatus(invoice.trustRequest?.trrStatus))}
        />
        <Field
          label="Total"
          value={
            invoice.trustRequest?.state === 'not_created'
              ? '-'
              : invoice.trustRequest?.total != null
                ? formatCurrency(invoice.trustRequest.total)
                : '-'
          }
        />
        <Field
          label="Paid"
          value={
            invoice.trustRequest?.state === 'not_created'
              ? '-'
              : invoice.trustRequest?.paid != null
                ? formatCurrency(invoice.trustRequest.paid)
                : '-'
          }
        />
        <Field
          label="Issued"
          value={
            invoice.trustRequest?.issued_at
              ? formatDateTime(invoice.trustRequest.issued_at).date
              : '-'
          }
        />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delivery helpers (inlined to avoid circular deps with the page)
// ---------------------------------------------------------------------------

function formatDeliveryStatusBucket(statusBucket?: string): string {
  switch ((statusBucket || '').toLowerCase()) {
    case 'delivery_notice_received':
      return 'Delivered';
    case 'partial_delivery_notice':
      return 'Partial';
    case 'failed':
      return 'Failed';
    default:
      return 'Pending';
  }
}

function getDeliveryStatusBadgeClass(statusBucket?: string): string {
  switch ((statusBucket || '').toLowerCase()) {
    case 'delivery_notice_received':
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    case 'partial_delivery_notice':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    case 'failed':
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    default:
      return 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600 text-xtnd-dark-600 dark:text-xtnd-dark-200';
  }
}

function formatDateShort(dateString?: string | null): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatDateTimeStacked(dateString?: string | null): { date: string; time: string } | null {
  if (!dateString) return null;
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
  };
}

function getJuniperStatusBadgeClass(status: string): string {
  const base = 'inline-flex px-2 py-0.5 text-xs font-semibold rounded-full';
  switch (status.toLowerCase()) {
    case 'completed':
    case 'email sent':
    case 'archived':
      return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    case 'processing':
    case 'pdf ready':
      return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
    case 'trust pending approval':
    case 'trust request approved':
    case 'email held':
      return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
    case 'failed':
    case 'trust request deleted':
    case 'trust request voided':
    case 'operational bill voided':
    case 'operational bill deleted':
      return `${base} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
    default:
      return `${base} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
  }
}

function getBillStatusBadgeClass(status: string): string {
  const base = 'inline-flex px-2 py-0.5 text-xs font-semibold rounded-full';
  switch (status.toLowerCase()) {
    case 'paid':
    case 'not required':
      return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    case 'partial payment':
    case 'approved':
    case 'sent':
      return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
    case 'pending_payment':
      return `${base} bg-lime-100 text-lime-800 dark:bg-lime-900 dark:text-lime-200`;
    case 'awaiting payment':
    case 'draft':
    case 'awaiting approval':
      return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
    case 'not created':
      return `${base} bg-xtnd-dark-50 text-xtnd-dark-500 dark:bg-xtnd-dark-700 dark:text-xtnd-dark-200`;
    case 'cancelled':
    case 'void':
    case 'voided':
    case 'deleted':
      return `${base} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
    default:
      return `${base} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
  }
}

function getMatterTypeBadgeClass(matterType?: string): string {
  const base = 'inline-flex px-2 py-0.5 text-xs font-semibold rounded-full';
  switch (matterType?.toLowerCase()) {
    case 'evergreen':
      return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
    case 'operational':
      return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
    default:
      return `${base} bg-xtnd-dark-50 text-xtnd-dark-700 dark:bg-xtnd-dark-800 dark:text-xtnd-dark-100`;
  }
}

function EmailPanel({
  invoice,
  deliveryDetails,
  isLoading,
}: {
  invoice: TransformedInvoice;
  deliveryDetails?: TransactionDeliveryDetails | null;
  isLoading?: boolean;
}) {
  const recipients = invoice.billRecipientEmails || [];

  return (
    <div className="space-y-5 text-sm">
      {/* Status */}
      <Section title="Email Status">
        <Field label="Juniper Status" value={invoice.juniperStatus || 'Unknown'} />
        <Field label="Email Queued" value={invoice.emailQueued ? 'Yes' : 'No'} />
        <Field label="Last Sent" value={invoice.lastSentAt || 'Not sent'} />
      </Section>

      {/* Recipients */}
      <Section title="Recipients">
        {recipients.length === 0 ? (
          <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
            No bill recipients found
          </div>
        ) : (
          <div className="space-y-1.5">
            {recipients.map((r: any, i: number) => {
              const email = typeof r === 'string' ? r : r?.primary_email_address;
              const name = typeof r === 'object' ? r?.name : undefined;
              const isBillRecipient = typeof r === 'object' ? r?.is_bill_recipient : true;
              return (
                <div
                  key={i}
                  className="flex items-center justify-between py-1 px-2 rounded bg-xtnd-dark-50 dark:bg-xtnd-dark-700"
                >
                  <div className="truncate">
                    {name && (
                      <span className="text-sm font-medium text-xtnd-dark-800 dark:text-xtnd-dark-50 mr-1.5">
                        {name}
                      </span>
                    )}
                    <span className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                      {email || 'No email'}
                    </span>
                  </div>
                  {!isBillRecipient && (
                    <span className="text-xs text-amber-600 dark:text-amber-400 ml-2 flex-shrink-0">
                      Not bill recipient
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Delivery Details */}
      <Section title="Delivery">
        {isLoading ? (
          <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
            Loading delivery details...
          </div>
        ) : deliveryDetails ? (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded border border-xtnd-dark-100 dark:border-xtnd-dark-600">
                <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Delivered</div>
                <div className="text-lg font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50">
                  {deliveryDetails.deliveredToRecipientsCount} / {deliveryDetails.totalToRecipients}
                </div>
              </div>
              <div className="p-3 rounded border border-xtnd-dark-100 dark:border-xtnd-dark-600">
                <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">Missing</div>
                <div className="text-lg font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50">
                  {deliveryDetails.missingToRecipientsCount}
                </div>
              </div>
            </div>

            {/* Status badge + latest event */}
            <div className="flex items-center justify-between">
              <span
                className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getDeliveryStatusBadgeClass(deliveryDetails.statusBucket)}`}
              >
                {formatDeliveryStatusBucket(deliveryDetails.statusBucket)}
              </span>
              <span className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                Latest: {formatDateShort(deliveryDetails.latestSesEventTimestamp)}
              </span>
            </div>

            {/* Recipients table */}
            <div>
              <h4 className="text-sm font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50 mb-2">
                TO Recipients
              </h4>
              {deliveryDetails.recipientStatuses.length === 0 ? (
                <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                  No TO recipients found.
                </p>
              ) : (
                <div className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                      <tr>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Recipient
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Status
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Event Type
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Failure Reason
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Last Event
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryDetails.recipientStatuses.map(recipient => (
                        <tr
                          key={recipient.recipient}
                          className="border-t border-xtnd-dark-100 dark:border-xtnd-dark-600"
                        >
                          <td className="px-3 py-2 text-xtnd-dark-800 dark:text-xtnd-dark-50">
                            {recipient.recipient}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getDeliveryStatusBadgeClass(
                                recipient.status === 'delivered'
                                  ? 'delivery_notice_received'
                                  : recipient.status === 'failed'
                                    ? 'failed'
                                    : 'sent_no_notice'
                              )}`}
                            >
                              {recipient.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                            {recipient.status === 'failed' && recipient.eventType ? (
                              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                                {recipient.eventType}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td
                            className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200 max-w-[200px] truncate"
                            title={
                              recipient.status === 'failed' ? recipient.failureReason || '' : ''
                            }
                          >
                            {recipient.status === 'failed'
                              ? recipient.failureReason || 'Details unavailable'
                              : '-'}
                          </td>
                          <td className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                            {formatDateShort(recipient.lastEventTimestamp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {deliveryDetails.followUpAttempts && deliveryDetails.followUpAttempts.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50 mb-2">
                  Follow-Up Attempts
                </h4>
                <div className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                      <tr>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Attempt
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Status
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Delivered
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Missing
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Sent
                        </th>
                        <th className="text-left px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                          Latest SES
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveryDetails.followUpAttempts.map(attempt => (
                        <tr
                          key={`${attempt.attemptNumber}-${attempt.emailId || 'na'}`}
                          className="border-t border-xtnd-dark-100 dark:border-xtnd-dark-600"
                        >
                          <td className="px-3 py-2 text-xtnd-dark-800 dark:text-xtnd-dark-50">
                            #{attempt.attemptNumber}
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getDeliveryStatusBadgeClass(attempt.statusBucket)}`}
                            >
                              {formatDeliveryStatusBucket(attempt.statusBucket)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                            {attempt.deliveredToRecipientsCount}
                          </td>
                          <td className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                            {attempt.missingToRecipientsCount}
                          </td>
                          <td className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                            {formatDateShort(attempt.sentAt)}
                          </td>
                          <td className="px-3 py-2 text-xtnd-dark-500 dark:text-xtnd-dark-200">
                            {formatDateShort(attempt.latestSesEventTimestamp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : invoice.deliverySummary ? (
          <>
            <Field
              label="Delivered"
              value={`${invoice.deliverySummary.deliveredToRecipientsCount} / ${invoice.deliverySummary.totalToRecipients}`}
            />
            <Field
              label="Status"
              value={formatDeliveryStatusBucket(invoice.deliverySummary.statusBucket)}
            />
          </>
        ) : (
          <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
            No delivery data available.
          </div>
        )}
      </Section>
    </div>
  );
}

function DocumentsPanel({
  invoice,
  rows,
  isLoading,
}: {
  invoice: TransformedInvoice;
  rows: TransactionDocumentHistoryRow[];
  isLoading?: boolean;
}) {
  const [loadingVersionId, setLoadingVersionId] = React.useState<string | null>(null);

  const formatSize = (size: number): string => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatSentColumn = (row: TransactionDocumentHistoryRow) => {
    if (row.sendStatus === 'not_sent') return { label: 'Not Sent', dt: null };
    const label = row.sendStatus === 'sent' ? 'Sent' : 'Partial';
    const dt = formatDateTimeStacked(row.lastSentAt);
    return { label, dt };
  };

  const openVersion = async (versionId: string) => {
    try {
      setLoadingVersionId(versionId);
      const res = await invoiceApi.getDocumentVersionDownload(invoice.id, versionId);
      const link = document.createElement('a');
      link.href = res.signedUrl;
      link.download = res.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setLoadingVersionId(null);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">Loading document history...</div>;
  }

  if (!rows.length) {
    return <div className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">No document history found for this transaction.</div>;
  }

  return (
    <div className="space-y-3 text-sm">
      <Section title="Generated Documents">
      <div className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
            <tr>
              <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">Type</th>
              <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200" />
              <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">Generated</th>
              <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">Sent</th>
              <th className="text-right px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const generated = formatDateTimeStacked(row.generatedAt);
              const sent = formatSentColumn(row);
              return (
                <tr
                  key={row.versionId}
                  className={`border-t border-xtnd-dark-100 dark:border-xtnd-dark-600 align-top ${row.isCurrent ? 'bg-xtnd-blue/10' : ''}`}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium capitalize text-xtnd-dark-700 dark:text-xtnd-dark-100">{row.docType}</div>
                    <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">{formatSize(row.size)}</div>
                  </td>
                  <td className="px-3 py-2 text-xtnd-dark-700 dark:text-xtnd-dark-100">
                    v{row.version}
                  </td>
                  <td className="px-3 py-2 text-xtnd-dark-700 dark:text-xtnd-dark-100">
                    {generated ? (
                      <>
                        <div>{generated.date}</div>
                        <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">{generated.time}</div>
                      </>
                    ) : 'N/A'}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-xtnd-dark-700 dark:text-xtnd-dark-100">{sent.label}</div>
                    {sent.dt && (
                      <>
                        <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">{sent.dt.date}</div>
                        <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">{sent.dt.time}</div>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => openVersion(row.versionId)}
                        disabled={loadingVersionId === row.versionId}
                        className="p-1 rounded hover:bg-xtnd-dark-100 dark:hover:bg-xtnd-dark-600 text-xtnd-blue dark:text-xtnd-blue-300 disabled:opacity-50"
                        title="Download"
                      >
                        <Icon name="HiDownload" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action Button (reusable for the Actions panel)
// ---------------------------------------------------------------------------

function ActionButton({
  icon,
  iconColor,
  label,
  onClick,
  disabled,
  title,
  danger,
}: {
  icon: string;
  iconColor: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${
        disabled
          ? 'opacity-40 cursor-not-allowed text-xtnd-dark-300 dark:text-xtnd-dark-500'
          : danger
            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
            : 'text-xtnd-dark-700 dark:text-xtnd-dark-200 hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-700'
      }`}
    >
      <Icon
        name={icon}
        className={`h-5 w-5 flex-shrink-0 ${disabled ? 'text-xtnd-dark-300 dark:text-xtnd-dark-500' : iconColor}`}
      />
      <span>{label}</span>
    </button>
  );
}

function ActionDivider() {
  return <div className="border-t border-xtnd-dark-200 dark:border-xtnd-dark-600 my-1" />;
}

function ActionsPanel({
  invoice,
  actions,
}: {
  invoice: TransformedInvoice;
  actions: TransactionDrawerActions;
}) {
  const hasPdf = invoice.pdfs?.invoice;
  const isValidPdfStatus =
    invoice.juniperStatus?.toLowerCase().includes('email held') ||
    invoice.juniperStatus?.toLowerCase().includes('completed');
  const canViewPdf = hasPdf && isValidPdfStatus;
  const receiptCount = invoice.documents?.storedAssets?.receipts?.length || 0;

  return (
    <div className="space-y-1 -mx-1">
      {/* View in Clio */}
      <ActionButton
        icon="HiFolder"
        iconColor="text-xtnd-blue"
        label="View in Clio"
        onClick={() => actions.onViewInClio(invoice)}
        disabled={!invoice.matterId || invoice.matterId === 'Unknown'}
        title={
          !invoice.matterId || invoice.matterId === 'Unknown'
            ? 'Matter ID not available'
            : 'Open matter in Clio'
        }
      />

      {/* View/Download Invoice PDF */}
      <ActionButton
        icon="HiDownload"
        iconColor={canViewPdf ? 'text-green-600' : 'text-xtnd-dark-300'}
        label="View/Download Invoice"
        onClick={() => actions.onViewPdf(invoice)}
        disabled={!canViewPdf}
        title={
          !canViewPdf
            ? "PDF not available - Transaction must be 'email-held' or 'completed' and have PDF"
            : ''
        }
      />

      {/* Download Receipts ZIP */}
      {receiptCount > 0 && (
        <ActionButton
          icon="HiDownload"
          iconColor="text-green-500"
          label={`Download ${receiptCount} Receipt${receiptCount > 1 ? 's' : ''} (ZIP)`}
          onClick={() => actions.onDownloadReceipts(invoice)}
        />
      )}

      <ActionDivider />

      {/* Send Email */}
      <ActionButton
        icon="HiMail"
        iconColor={
          invoice.juniperStatus?.toLowerCase().includes('email held')
            ? 'text-yellow-500'
            : invoice.juniperStatus?.toLowerCase().includes('completed')
              ? 'text-green-500'
              : 'text-xtnd-dark-300'
        }
        label={invoice.juniperStatus?.toLowerCase() === 'completed' ? 'Send Payment Reminder' : 'Send Email'}
        onClick={() => actions.onSendEmail(invoice)}
        disabled={
          actions.isResendPending ||
          invoice.emailQueued ||
          !actions.isResendEligible(invoice) ||
          !actions.hasValidRecipients(invoice)
        }
        title={
          !actions.isResendEligible(invoice)
            ? 'Only available for Trust Request bills in awaiting_payment or paid status, Email Held bills, or Completed transactions'
            : !actions.hasValidRecipients(invoice)
              ? 'No valid email recipients'
              : 'Send email or payment reminder'
        }
      />

      {/* Mark Completed */}
      {actions.isMarkCompletedEligible(invoice) && (
        <ActionButton
          icon="HiCheckCircle"
          iconColor="text-green-500"
          label="Mark Completed"
          onClick={() => actions.onMarkCompleted(invoice)}
          disabled={actions.isMarkCompletedPending}
          title="Mark as completed without sending email"
        />
      )}

      {/* View Bank Transactions */}
      <ActionButton
        icon="HiCreditCard"
        iconColor="text-xtnd-blue"
        label="View Bank Transactions"
        onClick={() => actions.onViewBankTransactions(invoice)}
        disabled={!invoice.matterId || invoice.matterId === 'Unknown'}
        title={
          !invoice.matterId || invoice.matterId === 'Unknown'
            ? 'Matter ID not available'
            : 'View bank transactions for this matter'
        }
      />

      <ActionDivider />

      {/* Regenerate PDF */}
      <ActionButton
        icon="HiRefresh"
        iconColor={
          actions.isPdfRegenerationEligible(invoice) ? 'text-orange-500' : 'text-xtnd-dark-300'
        }
        label="Regenerate PDF"
        onClick={() => actions.onRegeneratePdf(invoice)}
        disabled={actions.isRegeneratePending || !actions.isPdfRegenerationEligible(invoice)}
        title={
          !actions.isPdfRegenerationEligible(invoice)
            ? 'Only available for Evergreen bills in awaiting_payment, paid, or not_required status, or Operational bills in Email Held status'
            : 'Generate fresh PDF with new Clio Payment link'
        }
      />

      {/* Refresh from Clio */}
      <ActionButton
        icon="HiRefresh"
        iconColor="text-xtnd-blue"
        label="Refresh from Clio"
        onClick={() => actions.onRefreshFromClio(invoice)}
        disabled={actions.isReprocessing}
      />

      {/* Void/Delete Trust Request */}
      {actions.isVoidDeleteEligible(invoice) && (
        <>
          <ActionDivider />
          <ActionButton
            icon="HiTrash"
            iconColor="text-red-500"
            label={`${actions.getVoidDeleteAction(invoice)} Trust Request`}
            onClick={() => actions.onVoidDeleteTrustRequest(invoice)}
            disabled={actions.isVoidDeletePending}
            title={`${actions.getVoidDeleteAction(invoice)} the trust request bill in Clio`}
            danger
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HistoryPanel — TRR replacement state + trustRequestHistory + last payments
// ---------------------------------------------------------------------------

function trrReplacementBadgeClass(status: string | undefined): string {
  switch (status) {
    case 'creating_new_trr':
    case 'awaiting_new_trr_webhook':
      return 'inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    case 'completed':
      return 'inline-block px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
    case 'failed':
      return 'inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    default:
      return 'inline-block px-2 py-0.5 rounded text-xs font-medium bg-xtnd-dark-100 text-xtnd-dark-700 dark:bg-xtnd-dark-700 dark:text-xtnd-dark-100';
  }
}

function trrReplacementStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'creating_new_trr': return 'Creating new TRR…';
    case 'awaiting_new_trr_webhook': return 'Waiting for Clio webhook…';
    case 'completed': return 'Completed';
    case 'failed': return 'Failed';
    default: return status || '—';
  }
}

function trrReplacementIntentLabel(intent: string | undefined): string {
  switch (intent) {
    case 'send_reminder': return 'Send reminder';
    case 'send_initial': return 'Send initial';
    case 'regenerate_pdf': return 'Regenerate PDF';
    default: return intent || '—';
  }
}

function HistoryPanel({
  invoice,
  formatCurrency,
  formatDateTime,
}: {
  invoice: TransformedInvoice;
  formatCurrency: (a: number) => string;
  formatDateTime: (d: string | null | undefined) => { date: string; time: string };
}) {
  const replacement = invoice.trrReplacement;
  const history = invoice.trustRequestHistory || [];
  const lastOpAt = invoice.lastOperationalPaymentAt;
  const lastTrustAt = invoice.lastTrustRequestPaymentAt;
  const hasAnyContent =
    !!replacement || history.length > 0 || !!lastOpAt || !!lastTrustAt;

  if (!hasAnyContent) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
        <Icon name="FaHourglassHalf" className="h-10 w-10 text-xtnd-dark-300 dark:text-xtnd-dark-500 mb-3" />
        <h4 className="text-sm font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50 mb-1">
          No history yet
        </h4>
        <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 max-w-[260px]">
          TRR replacements and payment activity will appear here once this transaction has any.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5 text-sm">
      {/* Replacement state */}
      {replacement && (
        <Section title="TRR Replacement">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">Status</span>
            <span className={trrReplacementBadgeClass(replacement.status)}>
              {trrReplacementStatusLabel(replacement.status)}
            </span>
          </div>
          <Field label="Mode" value={replacement.mode === 'supplemental' ? 'Supplemental (no void)' : 'Void & Replace'} />
          <Field label="Intent" value={trrReplacementIntentLabel(replacement.intent)} />
          <Field
            label="Old TRR"
            value={replacement.oldTrrBillId}
            href={replacement.oldTrrBillId ? `https://app.clio.com/bills/${replacement.oldTrrBillId}` : undefined}
            mono
          />
          {replacement.newTrrBillId && (
            <Field
              label="New TRR"
              value={replacement.newTrrBillId}
              href={`https://app.clio.com/bills/${replacement.newTrrBillId}`}
              mono
            />
          )}
          <Field label="Expected amount" value={formatCurrency(replacement.expectedAmount)} />
          <Field
            label="Started"
            value={(() => {
              const dt = formatDateTime(replacement.startedAt);
              return `${dt.date} ${dt.time}`.trim();
            })()}
          />
          {replacement.completedAt && (
            <Field
              label="Completed"
              value={(() => {
                const dt = formatDateTime(replacement.completedAt);
                return `${dt.date} ${dt.time}`.trim();
              })()}
            />
          )}
          {replacement.lastError && (
            <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="text-xs font-semibold text-red-700 dark:text-red-300 mb-0.5">Last error</div>
              <div className="text-xs text-red-700 dark:text-red-300 break-words">{replacement.lastError}</div>
            </div>
          )}
        </Section>
      )}

      {/* Prior trust requests */}
      {history.length > 0 && (
        <Section title="Prior Trust Requests">
          <div className="border border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                <tr>
                  <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">TRR</th>
                  <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">State</th>
                  <th className="text-right px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">Due</th>
                  <th className="text-right px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">Paid</th>
                  <th className="text-left px-3 py-2 text-xs text-xtnd-dark-500 dark:text-xtnd-dark-200">Replaced</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry, idx) => {
                  const dt = formatDateTime(entry.replacedAt);
                  return (
                    <tr key={`${entry.trustRequestId}-${idx}`} className="border-t border-xtnd-dark-100 dark:border-xtnd-dark-600 align-top">
                      <td className="px-3 py-2">
                        <a
                          href={`https://app.clio.com/bills/${entry.trustRequestId}`}
                          target="clio-tab"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                        >
                          {entry.billNumber ? `#${entry.billNumber}` : entry.trustRequestId}
                        </a>
                        <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
                          <span className="font-mono">{entry.trustRequestId}</span>
                        </div>
                        <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 capitalize">
                          {entry.replacementMode === 'supplemental' ? 'supplemental' : 'void & replace'}
                        </div>
                      </td>
                      <td className="px-3 py-2 capitalize text-xtnd-dark-700 dark:text-xtnd-dark-100">
                        {entry.state || '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xtnd-dark-700 dark:text-xtnd-dark-100">
                        {entry.due != null ? formatCurrency(entry.due) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xtnd-dark-700 dark:text-xtnd-dark-100">
                        {entry.paid != null ? formatCurrency(entry.paid) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xtnd-dark-700 dark:text-xtnd-dark-100">
                        <div>{dt.date}</div>
                        <div className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">{dt.time}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Last payment activity */}
      {(lastOpAt || lastTrustAt) && (
        <Section title="Last Payment Activity">
          {lastOpAt && (
            <Field
              label="Operational"
              value={(() => {
                const dt = formatDateTime(lastOpAt);
                const stamp = `${dt.date} ${dt.time}`.trim();
                return invoice.lastOperationalPaymentAmount != null
                  ? `${formatCurrency(invoice.lastOperationalPaymentAmount)} · ${stamp}`
                  : stamp;
              })()}
            />
          )}
          {lastTrustAt && (
            <Field
              label="Trust request"
              value={(() => {
                const dt = formatDateTime(lastTrustAt);
                const stamp = `${dt.date} ${dt.time}`.trim();
                return invoice.lastTrustRequestPaymentAmount != null
                  ? `${formatCurrency(invoice.lastTrustRequestPaymentAmount)} · ${stamp}`
                  : stamp;
              })()}
            />
          )}
        </Section>
      )}
    </div>
  );
}

function SmithPanel({ invoice }: { invoice: TransformedInvoice }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
      <Icon name="HiChat" className="h-10 w-10 text-xtnd-dark-300 dark:text-xtnd-dark-500 mb-3" />
      <h4 className="text-sm font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50 mb-1">
        SMITH Assistant
      </h4>
      <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 max-w-[220px]">
        Agent/chat assistance for transaction{' '}
        <span className="font-mono">{invoice.shortJuniperId || invoice.id}</span> will be available
        here.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  accentColor,
  children,
}: {
  title: string;
  accentColor?: 'blue' | 'green';
  children: React.ReactNode;
}) {
  const colorMap = {
    blue: 'text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green: 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
  };
  const accent = accentColor
    ? colorMap[accentColor]
    : 'text-xtnd-dark-600 dark:text-xtnd-dark-200 border-xtnd-dark-200 dark:border-xtnd-dark-600';
  return (
    <div>
      <h4
        className={`text-sm font-semibold uppercase tracking-wider pb-1.5 mb-2 border-b ${accent}`}
      >
        {title}
      </h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  href,
  badgeClass,
}: {
  label: string;
  value: string | undefined;
  mono?: boolean;
  href?: string;
  badgeClass?: string;
}) {
  const content = value || '-';
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 flex-shrink-0">
        {label}
      </span>
      {badgeClass ? (
        <span className={badgeClass}>{content}</span>
      ) : href ? (
        <a
          href={href}
          target="clio-tab"
          rel="noopener noreferrer"
          className={`text-sm text-blue-600 dark:text-blue-400 hover:underline text-right truncate ${mono ? 'font-mono' : ''}`}
        >
          {content}
        </a>
      ) : (
        <span
          className={`text-sm text-xtnd-dark-800 dark:text-xtnd-dark-50 text-right truncate ${mono ? 'font-mono' : ''}`}
        >
          {content}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmptyState
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
      <Icon
        name="HiCursorClick"
        className="h-10 w-10 text-xtnd-dark-300 dark:text-xtnd-dark-500 mb-3"
      />
      <h4 className="text-sm font-semibold text-xtnd-dark-800 dark:text-xtnd-dark-50 mb-1">
        No transaction selected
      </h4>
      <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 max-w-[200px]">
        Click a row in the table to inspect its details here.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Drawer Component
// ---------------------------------------------------------------------------

const TransactionDrawer = React.memo<TransactionDrawerProps>(
  ({
    activeRow,
    isOpen,
    activeTab,
    onTabChange,
    onClose,
    tenantId,
    formatRawStatus,
    formatCurrency,
    formatDateTime,
    getTransactionWarnings,
    actions,
    deliveryDetails,
    isLoadingDeliveryDetails,
    documentHistoryRows,
    isLoadingDocumentHistory,
  }) => {
    const drawerRef = React.useRef<HTMLDivElement>(null);

    // Derive available tabs
    const isSmithAvailable = tenantId === SMITH_TENANT;

    // Ensure we don't land on smith tab for non-eligible tenants
    const effectiveTab = activeTab === 'smith' && !isSmithAvailable ? 'details' : activeTab;

    const tabLabels: { id: DrawerTab; label: string }[] = [
      { id: 'details', label: 'Details' },
      { id: 'documents', label: 'Documents' },
      { id: 'email', label: 'Email' },
      { id: 'history', label: 'History' },
      { id: 'actions', label: 'Actions' },
      ...(isSmithAvailable ? [{ id: 'smith' as DrawerTab, label: 'SMITH' }] : []),
    ];

    // Close on Escape key
    React.useEffect(() => {
      if (!isOpen) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Focus trap within drawer panel
    React.useEffect(() => {
      if (!isOpen || !drawerRef.current) return;
      const panel = drawerRef.current;
      const handler = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const focusable = panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      panel.addEventListener('keydown', handler);
      return () => panel.removeEventListener('keydown', handler);
    }, [isOpen]);

    return (
      <div className="flex h-full">
        {/* Utility Rail */}
        <UtilityRail
          activeTab={effectiveTab}
          isOpen={isOpen}
          onTabChange={onTabChange}
          onClose={onClose}
          tenantId={tenantId}
        />

        {/* Drawer Panel */}
        {isOpen && (
          <div
            ref={drawerRef}
            role="complementary"
            aria-label={`Transaction ${effectiveTab} panel`}
            className="w-[440px] flex flex-col border-l border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-white dark:bg-xtnd-dark-800 overflow-hidden flex-shrink-0"
          >
            {/* Tab strip */}
            <div className="flex border-b border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
              {tabLabels.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTabChange(t.id)}
                  className={`flex-1 text-sm font-medium py-2.5 transition-colors border-b-2 ${
                    effectiveTab === t.id
                      ? 'border-xtnd-blue text-xtnd-blue dark:text-xtnd-blue-300'
                      : 'border-transparent text-xtnd-dark-400 dark:text-xtnd-dark-300 hover:text-xtnd-dark-600 dark:hover:text-xtnd-dark-100'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Matter / Bill reference header (visible on all tabs) */}
            {activeRow && (
              <div className="px-4 py-2.5 border-b border-xtnd-dark-200 dark:border-xtnd-dark-600 bg-xtnd-dark-50/50 dark:bg-xtnd-dark-700/50">
                {activeRow.matterId && activeRow.matterId !== 'Unknown' ? (
                  <a
                    href={`https://app.clio.com/matters/${activeRow.matterId}`}
                    target="clio-tab"
                    rel="noopener noreferrer"
                    className="text-base font-semibold text-blue-600 dark:text-blue-400 truncate hover:underline block"
                  >
                    {activeRow.matterName || 'Unknown Matter'}
                  </a>
                ) : (
                  <div className="text-base font-semibold text-xtnd-dark dark:text-xtnd-light truncate">
                    {activeRow.matterName || 'Unknown Matter'}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-1 text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                  <a
                    href={activeRow.operationalBill?.id ? `https://app.clio.com/bills/${activeRow.operationalBill.id}` : undefined}
                    target="clio-tab"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    <span className="font-medium text-blue-600 dark:text-blue-400">OP</span>{' '}
                    {activeRow.operationalBill?.invoiceNumber || 'N/A'}
                  </a>
                  {activeRow.trustRequest?.number ? (
                    <a
                      href={activeRow.trustRequest?.id ? `https://app.clio.com/bills/${activeRow.trustRequest.id}` : undefined}
                      target="clio-tab"
                      rel="noopener noreferrer"
                      className="hover:underline"
                    >
                      <span className="font-medium text-green-600 dark:text-green-400">TRR</span>{' '}
                      {activeRow.trustRequest.number}
                    </a>
                  ) : null}
                </div>
              </div>
            )}

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {!activeRow ? (
                <EmptyState />
              ) : effectiveTab === 'details' ? (
                <DetailsPanel
                  invoice={activeRow}
                  formatRawStatus={formatRawStatus}
                  formatCurrency={formatCurrency}
                  formatDateTime={formatDateTime}
                  getTransactionWarnings={getTransactionWarnings}
                />
              ) : effectiveTab === 'email' ? (
                <EmailPanel
                  invoice={activeRow}
                  deliveryDetails={deliveryDetails}
                  isLoading={isLoadingDeliveryDetails}
                />
              ) : effectiveTab === 'documents' ? (
                <DocumentsPanel
                  invoice={activeRow}
                  rows={documentHistoryRows || []}
                  isLoading={isLoadingDocumentHistory}
                />
              ) : effectiveTab === 'history' ? (
                <HistoryPanel
                  invoice={activeRow}
                  formatCurrency={formatCurrency}
                  formatDateTime={formatDateTime}
                />
              ) : effectiveTab === 'actions' && actions ? (
                <ActionsPanel invoice={activeRow} actions={actions} />
              ) : effectiveTab === 'smith' && isSmithAvailable ? (
                <SmithPanel invoice={activeRow} />
              ) : null}
            </div>
          </div>
        )}
      </div>
    );
  }
);

TransactionDrawer.displayName = 'TransactionDrawer';

export default TransactionDrawer;
export { TransactionDrawer, UtilityRail };
